/**
 * Topbar — expanded / compact icons / collapsed
 * date_of_change: 2025-09-04
 * 
 * Provides a collapsible top toolbar with three modes:
 * - expanded: Full toolbar with all controls visible
 * - icons: Compact icon-only view
 * - collapsed: Hidden (null render)
 */
import React, { useEffect, useState } from 'react';
import { useDock } from './useDock';
import { PanelLeft, PanelRight, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, MoreHorizontal } from 'lucide-react';

interface TopbarProps {
  toolbar: React.ReactNode;
  iconbar: React.ReactNode;
  onToggleLeftPanel?: () => void;
  onToggleRightPanel?: () => void;
  leftPanelCollapsed?: boolean;
  rightPanelCollapsed?: boolean;
  className?: string;
  leftPanelMode?: 'expanded' | 'icons' | 'collapsed';
  rightPanelMode?: 'expanded' | 'icons' | 'collapsed';
  compact?: boolean;
}

const WIDE_DESKTOP_MIN_WIDTH = 1200;

const readWideDesktopViewport = () => (
  typeof window !== 'undefined' && window.innerWidth >= WIDE_DESKTOP_MIN_WIDTH
);

export default function Topbar({
  toolbar,
  iconbar,
  onToggleLeftPanel,
  onToggleRightPanel,
  leftPanelCollapsed = false,
  rightPanelCollapsed = false,
  leftPanelMode = 'expanded',
  rightPanelMode = 'expanded',
  compact = false,
  className = ''
}: TopbarProps) {
  // Start every BattleRoom in the complete operating view. A toolbar may be
  // compacted for the current visit, but reopening the room must not hide its
  // primary map controls because of an old local preference.
  // date_of_change: 2026-10-03
  const { mode, setMode } = useDock('topbar', 'expanded', { persist: false });
  const [compactToolsExpanded, setCompactToolsExpanded] = useState<boolean | null>(null);
  // A short landscape window still has desktop-width room for the complete
  // toolbar. Reserve the compact default for tablet/phone widths while keeping
  // the user's in-session disclosure choice authoritative.
  // date_of_change: 2026-10-03
  const compactToolsAreExpanded = compactToolsExpanded ?? readWideDesktopViewport();
  const displayMode = compact ? (compactToolsAreExpanded ? 'expanded' : 'icons') : mode;

  useEffect(() => {
    if (!compact) {
      setCompactToolsExpanded(null);
    }
  }, [compact]);
  
  const cycleMode = () => {
    if (compact) {
      setCompactToolsExpanded((isExpanded) => !(isExpanded ?? readWideDesktopViewport()));
      return;
    }
    setMode(mode === 'expanded' ? 'icons' : mode === 'icons' ? 'collapsed' : 'expanded');
  };

  // When topbar is collapsed, show minimal controls
  if (displayMode === 'collapsed') {
    return (
      <div className="battle-room-layout__topbar battle-room-layout__topbar--collapsed w-full h-8 border-b flex items-center justify-between px-2">
        <button 
          onClick={onToggleLeftPanel}
          className="battle-room-layout__chrome-button p-1 rounded transition-colors"
          title="Toggle left panel"
        >
          {leftPanelMode === 'collapsed' ? 
            <ChevronRight size={16} /> : 
            <ChevronLeft size={16} />
          }
        </button>
        
        <button 
          onClick={cycleMode}
          className="battle-room-layout__chrome-button p-1 rounded transition-colors"
          title="Expand topbar"
        >
          <ChevronDown size={16} />
        </button>
        
        <button 
          onClick={onToggleRightPanel}
          className="battle-room-layout__chrome-button p-1 rounded transition-colors"
          title="Toggle right panel"
        >
          {rightPanelMode === 'collapsed' ? 
            <ChevronLeft size={16} /> : 
            <ChevronRight size={16} />
          }
        </button>
      </div>
    );
  }

  return (
    <div className={`battle-room-layout__topbar w-full border-b ${compact ? 'battle-room-layout__topbar--compact' : ''} ${className}`}>
      <div className="flex items-center gap-3 px-3 py-1.5">
        <div className="flex items-center gap-2 flex-shrink-0">
          {onToggleLeftPanel && (
            <button
              onClick={onToggleLeftPanel}
              className="battle-room-layout__chrome-button p-1.5 rounded-md transition-colors"
              title={
                leftPanelMode === 'collapsed' ? 'Show left panel' : 
                leftPanelMode === 'icons' ? 'Expand left panel' : 'Hide left panel'
              }
            >
              {leftPanelMode === 'collapsed' ? (
                <ChevronRight size={18} className="text-base-content/80" />
              ) : leftPanelMode === 'icons' ? (
                <PanelLeft size={18} className="text-base-content/80" />
              ) : (
                <ChevronLeft size={18} className="text-base-content/80" />
              )}
            </button>
          )}
        </div>

        <div className="min-w-0 flex-1">
          {displayMode === 'expanded' && toolbar}
          {displayMode === 'icons' && iconbar}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Topbar mode toggle */}
          <button
            onClick={cycleMode}
            className="battle-room-layout__chrome-button p-1.5 rounded-md transition-colors"
            title={displayMode === 'expanded' ? 'Show compact map tools' : 'Show all map tools'}
            aria-label={displayMode === 'expanded' ? 'Show compact map tools' : 'Show all map tools'}
          >
            {displayMode === 'expanded' ? (
              <ChevronUp size={18} className="text-base-content/80" />
            ) : (
              <MoreHorizontal size={18} className="text-base-content/80" />
            )}
          </button>
          
          {onToggleRightPanel && (
            <button
              onClick={onToggleRightPanel}
              className="battle-room-layout__chrome-button p-1.5 rounded-md transition-colors"
              title={
                rightPanelMode === 'collapsed' ? 'Show right panel' : 
                  rightPanelMode === 'icons' ? 'Expand right panel' : 'Hide right panel'
              }
            >
              {rightPanelMode === 'collapsed' ? (
                <ChevronLeft size={18} className="text-base-content/80" />
              ) : rightPanelMode === 'icons' ? (
                <PanelRight size={18} className="text-base-content/80" />
              ) : (
                <ChevronRight size={18} className="text-base-content/80" />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
